import { Direction } from '@angular/cdk/bidi';
import { Component, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication';
import { NetworkService } from '@core/services/network.service';
import { PwaUiService } from '@shared/services/pwa-ui.service';
import { ThemeService } from '@shared/services';
import { NttIconButton } from '@ui/button';
import { InstallBannerComponent } from '@shared/components/install-banner/install-banner';
import { UpdatePromptComponent } from '@shared/components/update-prompt/update-prompt';
import { OfflineComponent } from '@shared/components/offline/offline';
import { environment } from '../environments/environment';

@Component({
  selector: 'bcm-root',
  imports: [
    RouterOutlet,
    NttIconButton,
    RouterLink,
    InstallBannerComponent,
    UpdatePromptComponent,
    OfflineComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  host: {
    '(window:resize)': 'onViewportResize()',
  },
})
export class App {
  private static readonly RESPONSIVE_WINDOW_NAME = 'bacart-responsive';

  protected readonly title = signal('reseller-my');
  protected readonly environment = environment;
  protected readonly router = inject(Router);
  protected readonly themeService = inject(ThemeService);
  protected readonly direction = signal<Direction>(this._getInitialDirection());
  protected readonly network = inject(NetworkService);
  private readonly authService = inject(AuthenticationService);
  protected readonly _pwaUi = inject(PwaUiService);
  protected readonly isResponsivePopup =
    typeof window !== 'undefined' &&
    window.opener != null &&
    window.name === App.RESPONSIVE_WINDOW_NAME;
  protected readonly viewportWidth = signal(
    typeof window === 'undefined' ? 0 : window.innerWidth,
  );
  protected readonly viewportHeight = signal(
    typeof window === 'undefined' ? 0 : window.innerHeight,
  );

  constructor() {
    this.authService.initializeAuthStatus();
    this._applyDirection();
  }

  toggleTheme() {
    this.themeService.toggle();
  }

  toggleStoryBook() {
    this.router.navigate(['/storybook']);
  }

  toggleDirection() {
    this.direction.set(this.direction() === 'rtl' ? 'ltr' : 'rtl');
    this._applyDirection();
    this._saveDirection();
  }

  protected openResponsiveWindow(): void {
    if (this.isResponsivePopup) return;
    const width = 375;
    const height = 844;
    window.open(
      location.href,
      App.RESPONSIVE_WINDOW_NAME,
      `popup=yes,width=${width},height=${height},resizable=yes,scrollbars=yes`,
    );
  }

  protected onViewportResize(): void {
    this.viewportWidth.set(window.innerWidth);
    this.viewportHeight.set(window.innerHeight);
  }

  private _getInitialDirection(): Direction {
    const stored = localStorage.getItem('direction');
    return stored === 'ltr' ? 'ltr' : 'rtl';
  }

  private _applyDirection() {
    document.documentElement.setAttribute('dir', this.direction());
    document.documentElement.setAttribute(
      'lang',
      this.direction() === 'rtl' ? 'fa' : 'en',
    );
  }

  private _saveDirection() {
    localStorage.setItem('direction', this.direction());
  }
}
